import { logger } from '../../utils/logger.js'
import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { demTileCache } from '../terrain/dem-tile-cache.js'
import { DEM_TILE_SIZE } from '../terrain/dem-config.js'
import { formatDistance } from '../../lib/coordinates.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { CARRIERS } from './carrier-roaming-map.js'
import { encodeRGBA_BMP } from '../terrain/encode-bmp.js'
import { renderLOSTileGPU } from './cellular-los-gpu.js'
import { cellularTowerDb } from './cellular-tower-db.js'
import LOSWorker from './cellular-los-worker.ts?worker'
import type { TowerData } from './cellular-los-worker.js'

const log = logger.create('cellular')

const TOWER_SOURCE_ID = 'cellular-towers-geojson'
const LOS_PROTOCOL = 'cellcov'
const DEFAULT_OPACITY = 0.35
const LOS_MIN_ZOOM = 8
const LOS_MAX_ZOOM = 12
const TILE_SIZE = DEM_TILE_SIZE
const PAD_TILES = 1
const PADDED_SIZE = TILE_SIZE * (1 + 2 * PAD_TILES)
const WORKER_POOL_SIZE = 4
const MIN_LOS_RANGE_M = 2000

export const TECHNOLOGIES = ['LTE', 'GSM', 'UMTS', 'CDMA', 'NR'] as const
export type Technology = (typeof TECHNOLOGIES)[number]

const FRESNEL_MARGIN_M: Record<Technology, number> = {
  NR: 10,
  LTE: 25,
  UMTS: 30,
  CDMA: 30,
  GSM: 35,
}

const CARRIER_BIT: Record<string, number> = {}
CARRIERS.forEach((c, i) => {
  CARRIER_BIT[c.id] = 1 << i
})

const LAYER_PREFIX_LOS = 'cellular-los'
const LAYER_PREFIX_TOWERS = 'cellular-twr'

function losLayerId(carrierId: string): string {
  return `${LAYER_PREFIX_LOS}-${carrierId}`
}
function losSourceId(carrierId: string): string {
  return `${LAYER_PREFIX_LOS}-src-${carrierId}`
}
function towerLayerId(carrierId: string): string {
  return `${LAYER_PREFIX_TOWERS}-${carrierId}`
}

function carrierIconId(carrierId: string): string {
  return `cellular-icon-${carrierId}`
}

function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z)
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

function tileToLon(x: number, z: number): number {
  return (x / Math.pow(2, z)) * 360 - 180
}

function metersPerPixel(lat: number, zoom: number): number {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180) * 256) / (Math.pow(2, zoom) * TILE_SIZE)
}

function latLonToGlobalPixel(lat: number, lon: number, z: number): { px: number; py: number } {
  const n = Math.pow(2, z)
  const px = ((lon + 180) / 360) * n * TILE_SIZE
  const latRad = (lat * Math.PI) / 180
  const py = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n * TILE_SIZE
  return { px, py }
}

class CellularCoverageService {
  private map: maplibregl.Map | null = null
  private _enabledCarriers = new Set<string>()
  private _enabledTechnologies = new Set<Technology>(TECHNOLOGIES)
  private _opacity = DEFAULT_OPACITY
  private _showTowerCoverage = true
  private _showTowerLocations = false
  private iconsRegistered = false

  private settingsStorage = new SettingsStorage()
  private workers: Worker[] = []
  private workerIndex = 0
  private nextRequestId = 0
  private pendingRequests = new Map<string, AbortController>()
  private losCache = new Map<string, Uint8Array>()
  private static readonly LOS_CACHE_MAX = 128
  private transparentTile: ArrayBuffer | null = null
  private moveEndHandler: (() => void) | null = null

  get enabledCarriers(): ReadonlySet<string> {
    return this._enabledCarriers
  }
  get enabledTechnologies(): ReadonlySet<Technology> {
    return this._enabledTechnologies
  }
  get opacity(): number {
    return this._opacity
  }
  get showTowerCoverage(): boolean {
    return this._showTowerCoverage
  }
  get showTowerLocations(): boolean {
    return this._showTowerLocations
  }

  isCarrierEnabled(carrierId: string): boolean {
    return this._enabledCarriers.has(carrierId)
  }
  isTechnologyEnabled(tech: Technology): boolean {
    return this._enabledTechnologies.has(tech)
  }

  register(map: maplibregl.Map): void {
    this.map = map
    void cellularTowerDb.load()
    addProtocol(LOS_PROTOCOL, (params, abortController) => {
      return this.handleLOSTileRequest(params, abortController)
    })
    this.moveEndHandler = () => this.updateTowerDisplay()
    map.on('moveend', this.moveEndHandler)
  }

  unregister(): void {
    this.disableAll()
    if (this.map && this.moveEndHandler) {
      this.map.off('moveend', this.moveEndHandler)
      this.moveEndHandler = null
    }
    try {
      removeProtocol(LOS_PROTOCOL)
    } catch (_) {
      /* may not be registered */
    }
    this.terminateWorkers()
    this.losCache.clear()
    this.map = null
    this.iconsRegistered = false
  }

  private initWorkers(): void {
    if (this.workers.length > 0) return
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      this.workers.push(new LOSWorker())
    }
  }

  private getWorker(): Worker {
    const worker = this.workers[this.workerIndex]
    this.workerIndex = (this.workerIndex + 1) % this.workers.length
    return worker
  }

  private terminateWorkers(): void {
    this.workers.forEach(w => w.terminate())
    this.workers = []
    this.workerIndex = 0
  }

  private ensureTowerSource(): void {
    if (!this.map || this.map.getSource(TOWER_SOURCE_ID)) return
    this.map.addSource(TOWER_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }

  private removeTowerSourceIfUnused(): void {
    if (!this.map || this._enabledCarriers.size > 0) return
    if (this.map.getSource(TOWER_SOURCE_ID)) {
      this.map.removeSource(TOWER_SOURCE_ID)
    }
  }

  private updateTowerDisplay(): void {
    if (!this.map || !this._showTowerLocations || this._enabledCarriers.size === 0) return
    if (!cellularTowerDb.ready) return

    const bounds = this.map.getBounds()
    const towers = cellularTowerDb.queryTowersForDisplay(
      {
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth(),
      },
      this._enabledCarriers,
      this._enabledTechnologies
    )

    const features: GeoJSON.Feature[] = towers.map(t => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [t.lon, t.lat] },
      properties: {
        carrier: CARRIERS.find(c => c.id === t.carrierId)?.name ?? t.carrierId,
        carrierId: t.carrierId,
        technology: t.radio,
        range_m: t.range,
        signal: t.signal,
        mcc: t.mcc,
        net: t.net,
        area: t.area,
        cell: t.cell,
      },
    }))

    const src = this.map.getSource(TOWER_SOURCE_ID)
    if (src && 'setData' in src) {
      ;(src as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features })
    }
  }

  private async ensureIcons(): Promise<void> {
    if (!this.map || this.iconsRegistered) return
    const promises = CARRIERS.map(c => this.registerCarrierIcon(c.id, c.color))
    await Promise.all(promises)
    this.iconsRegistered = true
  }

  private registerCarrierIcon(carrierId: string, color: string): Promise<void> {
    const map = this.map!
    const imageId = carrierIconId(carrierId)
    if (map.hasImage(imageId)) return Promise.resolve()

    const dpr = window.devicePixelRatio || 1
    const size = 24
    const canvas = document.createElement('canvas')
    canvas.width = size * dpr
    canvas.height = size * dpr
    const ctx = canvas.getContext('2d')!
    ctx.scale(dpr, dpr)

    const cx = size / 2
    const cy = size / 2
    const r = size / 2 - 2

    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 1.5
    ctx.stroke()

    return new Promise<void>(resolve => {
      const draw = (): void => {
        ctx.font = '13px "Material Symbols Outlined"'
        ctx.fillStyle = '#ffffff'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('cell_tower', cx, cy)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        map.addImage(imageId, imageData, { pixelRatio: dpr })
        resolve()
      }

      if (!document.fonts.check('13px "Material Symbols Outlined"')) {
        document.fonts.load('13px "Material Symbols Outlined"').then(draw).catch(draw)
      } else {
        draw()
      }
    })
  }

  // ── LOS coverage (per-pixel raster from DEM) ──

  private addLOSLayer(carrierId: string): void {
    if (!this.map || !this._showTowerCoverage) return
    const carrier = CARRIERS.find(c => c.id === carrierId)
    if (!carrier) return

    const srcId = losSourceId(carrierId)
    const layId = losLayerId(carrierId)
    if (this.map.getLayer(layId)) return

    if (!this.map.getSource(srcId)) {
      this.map.addSource(srcId, {
        type: 'raster',
        tiles: [`${LOS_PROTOCOL}://${carrierId}/{z}/{x}/{y}`],
        tileSize: TILE_SIZE,
        minzoom: LOS_MIN_ZOOM,
        maxzoom: LOS_MAX_ZOOM,
        attribution: 'Elevation: AWS Terrain Tiles',
      })
    }

    addOverlayLayer(
      this.map,
      {
        id: layId,
        type: 'raster',
        source: srcId,
        minzoom: LOS_MIN_ZOOM,
        paint: {
          'raster-opacity': this._opacity * 0.7,
          'raster-fade-duration': 150,
        },
      },
      'cellular'
    )
  }

  private removeLOSLayer(carrierId: string): void {
    if (!this.map) return
    const layId = losLayerId(carrierId)
    const srcId = losSourceId(carrierId)
    if (this.map.getLayer(layId)) this.map.removeLayer(layId)
    if (this.map.getSource(srcId)) this.map.removeSource(srcId)
  }

  // ── Tower locations (icon markers from GeoJSON source) ──

  private addTowerLayer(carrierId: string): void {
    if (!this.map || !this._showTowerLocations) return
    const carrier = CARRIERS.find(c => c.id === carrierId)
    if (!carrier) return
    const id = towerLayerId(carrierId)
    if (this.map.getLayer(id)) return

    this.ensureTowerSource()

    addOverlayLayer(
      this.map,
      {
        id,
        type: 'symbol',
        source: TOWER_SOURCE_ID,
        minzoom: 8,
        filter: ['==', ['get', 'carrierId'], carrierId],
        layout: {
          'icon-image': carrierIconId(carrierId),
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': false,
        },
      },
      'cellular'
    )
    this.updateTowerDisplay()
  }

  private removeTowerLayer(carrierId: string): void {
    if (!this.map) return
    const id = towerLayerId(carrierId)
    if (this.map.getLayer(id)) this.map.removeLayer(id)
  }

  // ── Layer type toggles ──

  setShowTowerCoverage(show: boolean): void {
    if (show === this._showTowerCoverage) return
    this._showTowerCoverage = show
    for (const carrierId of this._enabledCarriers) {
      if (show) this.addLOSLayer(carrierId)
      else this.removeLOSLayer(carrierId)
    }
    log.info(`LOS coverage: ${show}`)
  }

  setShowTowerLocations(show: boolean): void {
    if (show === this._showTowerLocations) return
    this._showTowerLocations = show
    for (const carrierId of this._enabledCarriers) {
      if (show) this.addTowerLayer(carrierId)
      else this.removeTowerLayer(carrierId)
    }
    log.info(`Tower locations: ${show}`)
  }

  // ── Technology filtering ──

  toggleTechnology(tech: Technology): void {
    if (this._enabledTechnologies.has(tech)) {
      this._enabledTechnologies.delete(tech)
    } else {
      this._enabledTechnologies.add(tech)
    }
    this.refreshFilters()
    this.invalidateLOSCache()
    log.info(`Technology ${tech}: ${this._enabledTechnologies.has(tech)}`)
  }

  setEnabledTechnologies(techs: Technology[]): void {
    this._enabledTechnologies.clear()
    for (const t of techs) this._enabledTechnologies.add(t)
    this.refreshFilters()
    this.invalidateLOSCache()
  }

  private refreshFilters(): void {
    if (!this.map) return
    this.updateTowerDisplay()
  }

  private invalidateLOSCache(): void {
    this.losCache.clear()
    if (!this.map) return
    for (const carrierId of this._enabledCarriers) {
      const srcId = losSourceId(carrierId)
      const src = this.map.getSource(srcId)
      if (src) {
        this.removeLOSLayer(carrierId)
        this.addLOSLayer(carrierId)
      }
    }
  }

  // ── Carrier management ──

  async enableCarrier(carrierId: string): Promise<void> {
    if (!this.map || this._enabledCarriers.has(carrierId)) return
    const carrier = CARRIERS.find(c => c.id === carrierId)
    if (!carrier) return

    this._enabledCarriers.add(carrierId)
    await this.ensureIcons()
    this.addLOSLayer(carrierId)
    this.addTowerLayer(carrierId)

    log.info(`Enabled carrier: ${carrier.name}`)
  }

  disableCarrier(carrierId: string): void {
    if (!this.map) return
    this._enabledCarriers.delete(carrierId)
    this.removeLOSLayer(carrierId)
    this.removeTowerLayer(carrierId)
    this.removeTowerSourceIfUnused()
  }

  toggleCarrier(carrierId: string): void {
    if (this._enabledCarriers.has(carrierId)) this.disableCarrier(carrierId)
    else void this.enableCarrier(carrierId)
  }

  enableCarriers(carrierIds: string[]): void {
    for (const id of carrierIds) void this.enableCarrier(id)
  }

  disableAll(): void {
    for (const carrierId of [...this._enabledCarriers]) {
      this.disableCarrier(carrierId)
    }
  }

  toggleAll(): void {
    const allEnabled = CARRIERS.every(c => this._enabledCarriers.has(c.id))
    if (allEnabled) {
      this.disableAll()
    } else {
      for (const c of CARRIERS) void this.enableCarrier(c.id)
    }
  }

  // ── Opacity ──

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map) return
    for (const carrierId of this._enabledCarriers) {
      const layId = losLayerId(carrierId)
      if (this.map.getLayer(layId)) {
        this.map.setPaintProperty(layId, 'raster-opacity', this._opacity * 0.7)
      }
    }
  }

  // ── Re-add after style change ──

  reAdd(): void {
    if (!this.map) return
    this.iconsRegistered = false
    const enabledIds = [...this._enabledCarriers]
    this._enabledCarriers.clear()
    for (const id of enabledIds) {
      void this.enableCarrier(id)
    }
  }

  // ── Query + popup ──

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map) return []
    const layers: string[] = []
    for (const id of this._enabledCarriers) {
      const layerId = towerLayerId(id)
      if (this.map.getLayer(layerId)) layers.push(layerId)
    }
    if (layers.length === 0) return []
    return this.map.queryRenderedFeatures(point, { layers })
  }

  buildPopupHtml(features: maplibregl.MapGeoJSONFeature[]): string | null {
    if (features.length === 0) return null

    const parts: string[] = []
    const prefs = this.settingsStorage.getSettingsWithDefaults()

    for (const f of features) {
      const p = f.properties
      const carrier = p.carrier as string
      const tech = (p.technology as string) || ''
      const rangeM = p.range_m ?? 0
      const signal = p.signal ?? 0

      let detail = `<strong>${carrier}</strong>`
      if (tech) detail += ` · ${tech}`
      if (rangeM > 0) detail += `<br>Range: ${formatDistance(rangeM as number, prefs)}`
      if (signal > 0) detail += ` · Signal: ${signal} dBm`

      const geom = f.geometry as GeoJSON.Geometry | undefined
      if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
        const [lon, lat] = geom.coordinates
        detail += `<br><span style="font-size:11px;opacity:0.7">${lat.toFixed(5)}, ${lon.toFixed(5)}</span>`
      }

      parts.push(`<div style="margin-bottom:6px">${detail}</div>`)
    }

    return parts.length > 0 ? `<div class="cairn-cellular-popup">${parts.join('')}</div>` : null
  }

  // ── LOS tile protocol ──

  private queryTowersForTile(
    z: number,
    x: number,
    y: number,
    cellSizeM: number,
    carrierId?: string
  ): TowerData[] {
    if (!cellularTowerDb.ready) return []

    const tileLat0 = tileToLat(y, z)
    const tileLat1 = tileToLat(y + 1, z)
    const tileLon0 = tileToLon(x, z)
    const tileLon1 = tileToLon(x + 1, z)

    const bounds = {
      west: tileLon0,
      south: Math.min(tileLat0, tileLat1),
      east: tileLon1,
      north: Math.max(tileLat0, tileLat1),
    }

    const records = cellularTowerDb.queryTowersInExtent(
      bounds,
      carrierId,
      this._enabledTechnologies
    )

    const originGlobalPx = (x - PAD_TILES) * TILE_SIZE
    const originGlobalPy = (y - PAD_TILES) * TILE_SIZE

    const towers: TowerData[] = []
    for (const r of records) {
      const rangeM = Math.max(Math.min(r.range, 50000), MIN_LOS_RANGE_M)
      const global = latLonToGlobalPixel(r.lat, r.lon, z)
      const margin = FRESNEL_MARGIN_M[r.radio] ?? 25

      towers.push({
        demPx: global.px - originGlobalPx,
        demPy: global.py - originGlobalPy,
        rangePixels: rangeM / cellSizeM,
        carrierBit: CARRIER_BIT[r.carrierId],
        marginM: margin,
      })
    }

    if (towers.length > 2048) {
      log.warn(
        `tile ${z}/${x}/${y}: ${towers.length} towers EXCEEDS GPU limit of 2048 — coverage will be incomplete`
      )
    } else {
      log.info(`tile ${z}/${x}/${y}: ${towers.length} towers for carrier=${carrierId ?? 'all'}`)
    }
    return towers
  }

  private async fetchPaddedDem(
    z: number,
    x: number,
    y: number,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    const maxTile = Math.pow(2, z) - 1
    const padded = new Uint8ClampedArray(PADDED_SIZE * PADDED_SIZE * 4)

    const fetches: Promise<{
      dx: number
      dy: number
      pixels: Uint8ClampedArray | null
    }>[] = []

    for (let dy = -PAD_TILES; dy <= PAD_TILES; dy++) {
      for (let dx = -PAD_TILES; dx <= PAD_TILES; dx++) {
        const nx = x + dx
        const ny = y + dy
        if (ny < 0 || ny > maxTile) {
          fetches.push(Promise.resolve({ dx, dy, pixels: null }))
        } else {
          const wrappedX = ((nx % (maxTile + 1)) + (maxTile + 1)) % (maxTile + 1)
          fetches.push(
            demTileCache
              .getTile(z, wrappedX, ny, signal)
              .then(pixels => ({ dx, dy, pixels }))
              .catch(() => ({ dx, dy, pixels: null }))
          )
        }
      }
    }

    const results = await Promise.all(fetches)

    let centerPixels: Uint8ClampedArray | null = null
    for (const { dx, dy, pixels } of results) {
      if (!pixels) continue
      if (dx === 0 && dy === 0) centerPixels = pixels
      const offsetX = (dx + PAD_TILES) * TILE_SIZE
      const offsetY = (dy + PAD_TILES) * TILE_SIZE
      for (let row = 0; row < TILE_SIZE; row++) {
        const srcStart = row * TILE_SIZE * 4
        const dstStart = ((offsetY + row) * PADDED_SIZE + offsetX) * 4
        padded.set(pixels.subarray(srcStart, srcStart + TILE_SIZE * 4), dstStart)
      }
    }

    if (centerPixels) {
      for (const { dx, dy, pixels } of results) {
        if (pixels) continue
        const offsetX = (dx + PAD_TILES) * TILE_SIZE
        const offsetY = (dy + PAD_TILES) * TILE_SIZE
        for (let row = 0; row < TILE_SIZE; row++) {
          const srcRow = Math.max(0, Math.min(TILE_SIZE - 1, row + dy * TILE_SIZE))
          for (let col = 0; col < TILE_SIZE; col++) {
            const srcCol = Math.max(0, Math.min(TILE_SIZE - 1, col + dx * TILE_SIZE))
            const srcIdx = (srcRow * TILE_SIZE + srcCol) * 4
            const dstIdx = ((offsetY + row) * PADDED_SIZE + offsetX + col) * 4
            padded[dstIdx] = centerPixels[srcIdx]
            padded[dstIdx + 1] = centerPixels[srcIdx + 1]
            padded[dstIdx + 2] = centerPixels[srcIdx + 2]
            padded[dstIdx + 3] = centerPixels[srcIdx + 3]
          }
        }
      }
    }

    return padded
  }

  private processInWorker(
    demPixels: Uint8ClampedArray,
    towers: TowerData[],
    cellSizeM: number,
    signal: AbortSignal
  ): Promise<Uint8Array> {
    this.initWorkers()
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      const requestId = this.nextRequestId++
      const worker = this.getWorker()

      const handler = (e: MessageEvent): void => {
        if (e.data.requestId !== requestId) return
        worker.removeEventListener('message', handler)
        signal.removeEventListener('abort', onAbort)
        resolve(new Uint8Array(e.data.coverage))
      }
      const onAbort = (): void => {
        worker.removeEventListener('message', handler)
        reject(new DOMException('Aborted', 'AbortError'))
      }

      worker.addEventListener('message', handler)
      signal.addEventListener('abort', onAbort)

      const buf = demPixels.buffer.slice(
        demPixels.byteOffset,
        demPixels.byteOffset + demPixels.byteLength
      )
      worker.postMessage(
        {
          demPixels: buf,
          paddedSize: PADDED_SIZE,
          tileSize: TILE_SIZE,
          tileOffsetX: PAD_TILES * TILE_SIZE,
          tileOffsetY: PAD_TILES * TILE_SIZE,
          cellSizeM,
          towers,
          requestId,
        },
        [buf]
      )
    })
  }

  private coverageToRGBA(coverage: Uint8Array, carrierId: string): Uint8ClampedArray {
    const carrier = CARRIERS.find(c => c.id === carrierId)
    const bit = CARRIER_BIT[carrierId]
    const rgba = new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * 4)

    if (!carrier) return rgba

    const r = parseInt(carrier.color.slice(1, 3), 16)
    const g = parseInt(carrier.color.slice(3, 5), 16)
    const b = parseInt(carrier.color.slice(5, 7), 16)

    for (let i = 0; i < coverage.length; i++) {
      if (coverage[i] & bit) {
        const j = i * 4
        rgba[j] = r
        rgba[j + 1] = g
        rgba[j + 2] = b
        rgba[j + 3] = 180
      }
    }

    return rgba
  }

  private async handleLOSTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ArrayBuffer }> {
    const match = params.url.match(/([a-z]+)\/(\d+)\/(\d+)\/(\d+)/)
    if (!match) {
      throw new Error(`Invalid cellcov tile URL: ${params.url}`)
    }

    const carrierId = match[1]
    const z = parseInt(match[2])
    const x = parseInt(match[3])
    const y = parseInt(match[4])
    const tileKey = `${z}/${x}/${y}`

    this.pendingRequests.set(`${carrierId}/${tileKey}`, abortController)

    try {
      const tTotal = performance.now()
      const cacheKey = `${carrierId}/${tileKey}`
      let coverageBits = this.losCache.get(cacheKey)

      if (!coverageBits) {
        const lat = tileToLat(y + 0.5, z)
        const cellSizeM = metersPerPixel(lat, z)

        const tDem = performance.now()
        const paddedDem = await this.fetchPaddedDem(z, x, y, abortController.signal)
        const demMs = performance.now() - tDem

        const towers = this.queryTowersForTile(z, x, y, cellSizeM, carrierId)

        const tRender = performance.now()
        let renderPath = 'skip'

        if (towers.length === 0) {
          coverageBits = new Uint8Array(TILE_SIZE * TILE_SIZE)
        } else {
          const gpuResult = renderLOSTileGPU(
            paddedDem,
            PADDED_SIZE,
            TILE_SIZE,
            PAD_TILES * TILE_SIZE,
            towers
          )
          if (gpuResult) {
            coverageBits = gpuResult
            renderPath = 'gpu'
          } else {
            coverageBits = await this.processInWorker(
              paddedDem,
              towers,
              cellSizeM,
              abortController.signal
            )
            renderPath = 'cpu'
          }
        }
        const renderMs = performance.now() - tRender

        if (this.losCache.size >= CellularCoverageService.LOS_CACHE_MAX) {
          const firstKey = this.losCache.keys().next().value!
          this.losCache.delete(firstKey)
        }
        this.losCache.set(cacheKey, coverageBits)

        let coveredPixels = 0
        for (let ci = 0; ci < coverageBits.length; ci++) {
          if (coverageBits[ci] !== 0) coveredPixels++
        }

        const totalMs = performance.now() - tTotal
        log.warn(
          `LOS tile ${z}/${x}/${y} [${renderPath}] total=${totalMs.toFixed(1)}ms dem=${demMs.toFixed(1)}ms render=${renderMs.toFixed(1)}ms towers=${towers.length} covered=${coveredPixels}`
        )
      }

      const rgba = this.coverageToRGBA(coverageBits, carrierId)

      let hasContent = false
      for (let i = 3; i < rgba.length; i += 4) {
        if (rgba[i] > 0) {
          hasContent = true
          break
        }
      }

      if (!hasContent) {
        return { data: await this.getTransparentTile() }
      }

      return { data: encodeRGBA_BMP(rgba, TILE_SIZE, TILE_SIZE) }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e
      log.warn(`tile ${carrierId}/${tileKey} failed:`, e)
      return { data: await this.getTransparentTile() }
    } finally {
      this.pendingRequests.delete(`${carrierId}/${tileKey}`)
    }
  }

  private async getTransparentTile(): Promise<ArrayBuffer> {
    if (this.transparentTile) return this.transparentTile
    const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, TILE_SIZE, TILE_SIZE)
    const blob = await canvas.convertToBlob({ type: 'image/png' })
    this.transparentTile = await blob.arrayBuffer()
    return this.transparentTile
  }
}

export const cellularCoverageService = new CellularCoverageService()
